import React from 'react'
import { API_RESORTS } from '@constants'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { useAuthHeaders } from '@hooks/useAuthHeaders'
import { serverUrl } from '@utils/MkEnv'
import { DataStore, EMkStorageKeys } from '@utils/DataStore'
import { TMkResort } from '@features/search/services/searchTypes'
import { useSiteContext } from '@contexts/MkSiteContext'
import { IResort } from '@resort/types'
import { mapResortToIResort } from '@utils/mapResortToIResort'
import { transformResortMedia } from '@utils/transformResortMedia'

export interface ResortRoomType {
  resourceKey: string | null
  roomType: string
  roomCategory: string | null
}

export interface Resort extends TMkResort {
  resourceKey: string
  prodName: string
  address: string
  zipcode: string
  countryFull: string
  stateFull: string
  phone: string
  latitude: string
  longitude: string
  averageRating?: number
  resortType?: string
  availableSuiteCount?: number
}

/*
 * allResorts: all possible resorts
 * filteredResorts: resorts filtered by region by frontend
 * availResorts: resorts from resortAvail API call
 * displayResorts: availResorts if present, otherwise filteredResorts (converted to IResort)
 */
interface ResortContextValue {
  allResorts: Resort[]
  availResorts: Resort[]
  filteredResorts: IResort[]
  displayResorts: IResort[]
  isLoading: boolean
  isDataProcessing: boolean
  setAllResorts: React.Dispatch<React.SetStateAction<Resort[]>>
  updateAllResorts: (newResorts: Resort[]) => void
  setAvailResorts: React.Dispatch<React.SetStateAction<Resort[]>>
  setFilteredResorts: (filteredResorts: IResort[]) => void
  setIsDataProcessing: React.Dispatch<React.SetStateAction<boolean>>
  getResortBykey: (resortKey: string) => Resort | undefined
  getResortById: (resortId: string) => Resort | undefined
}

const ResortContext = React.createContext<ResortContextValue | undefined>(undefined)

export const useResorts = (): ResortContextValue => {
  const context = React.useContext(ResortContext)
  if (!context) {
    throw new Error('useResorts must be used within a ResortProvider')
  }
  return context
}

export const ResortProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [allResorts, setAllResorts] = React.useState<Resort[]>(() => {
    const cached = DataStore.getItem<Resort[]>(EMkStorageKeys.MAKO_RESORTS)
    return cached || []
  })
  const [availResorts, setAvailResorts] = React.useState<Resort[]>([])
  const [filteredResorts, setFilteredResorts] = React.useState<IResort[]>([])
  const [displayResorts, setDisplayResorts] = React.useState<IResort[]>([])
  const { themeBrand: brand } = useSiteContext()
  const { isAuthenticated, isAuthorized, isAuthInitialized } = useAuthentication()
  const authHeaders = useAuthHeaders()
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [isDataProcessing, setIsDataProcessing] = React.useState<boolean>(false)
  const lastBrandRef = React.useRef<string | null>(null)
  const prevAuthorizedRef = React.useRef<boolean | undefined>(undefined)
  // Keep latest headers without retriggering the auth-refresh effect when JWT/session updates after login.
  const authHeadersRef = React.useRef(authHeaders)
  authHeadersRef.current = authHeaders

  // Clear any brand-dependent resort cache when switching brands.
  React.useEffect(() => {
    if (!brand) return

    if (lastBrandRef.current && lastBrandRef.current !== brand) {
      setAllResorts([])
      setAvailResorts([])
      setFilteredResorts([])
      setDisplayResorts([])
    }

    lastBrandRef.current = brand
  }, [brand])

  React.useEffect(() => {
    prevAuthorizedRef.current = undefined
  }, [brand])

  const updateAllResorts = React.useCallback((newResorts: Resort[]) => {
    setAllResorts(newResorts)
    DataStore.setItem(EMkStorageKeys.MAKO_RESORTS, newResorts)
  }, [])

  /** Single implementation for GET /resort list (anonymous or with auth headers). */
  const fetchResortsForAuthState = React.useCallback(
    async (withAuth: boolean, signal?: AbortSignal) => {
      const clearResortsOnFailure = () => {
        updateAllResorts([])
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(withAuth ? authHeadersRef.current : {}),
      }
      try {
        const response = await fetch(serverUrl + API_RESORTS(brand), { method: 'GET', headers, signal })
        if (!response.ok) {
          console.error(`[ResortContext] Failed to fetch resorts (${response.status} ${response.statusText})`)
          clearResortsOnFailure()
          return
        }
        const data = (await response.json()) as Resort[]
        updateAllResorts(data)
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return
        console.error('[ResortContext] Failed to fetch resorts:', err)
        clearResortsOnFailure()
      }
    },
    [brand, updateAllResorts],
  )

  // After login (authorized) or logout (anonymous), refresh the resort list so it matches API eligibility.
  // Deferred with requestIdleCallback so it does not compete with first paint or auth redirect work.
  React.useEffect(() => {
    if (!brand || !isAuthInitialized) return

    const was = prevAuthorizedRef.current
    prevAuthorizedRef.current = isAuthorized

    if (was === undefined && !isAuthorized) {
      return
    }
    if (was !== undefined && was === isAuthorized) {
      return
    }

    const withAuth = isAuthorized
    const abort = new AbortController()
    const runFetch = () => {
      void fetchResortsForAuthState(withAuth, abort.signal)
    }

    const win = typeof window !== 'undefined' ? window : undefined
    let cancelSchedule: (() => void) | undefined
    if (win && 'requestIdleCallback' in win) {
      const id = win.requestIdleCallback(runFetch, { timeout: 4000 })
      cancelSchedule = () => win.cancelIdleCallback(id)
    } else {
      const id = globalThis.setTimeout(runFetch, 0)
      cancelSchedule = () => globalThis.clearTimeout(id)
    }

    return () => {
      cancelSchedule?.()
      abort.abort()
    }
  }, [brand, isAuthorized, isAuthInitialized, fetchResortsForAuthState])

  // TODO: sort on server, not browser
  // https://wyndhamexchangeandrentals.atlassian.net/browse/MAK-2878
  // When authorized, the auth-refresh effect loads the list (with headers) — do not duplicate with an unauthenticated fetch here.
  // While Okta session exists but Mako authorization is still pending (isAuthenticated && !isAuthorized), skip — otherwise we fetch
  // a public list right before the authorized refresh runs after login.
  React.useEffect(() => {
    if (allResorts.length !== 0) return
    if (!isAuthInitialized) return
    if (isAuthorized) return
    if (isAuthenticated) return

    const abort = new AbortController()
    setIsLoading(true)
    void fetchResortsForAuthState(false, abort.signal).finally(() => {
      setIsLoading(false)
    })

    return () => {
      abort.abort()
    }
  }, [brand, allResorts.length, isAuthInitialized, isAuthenticated, isAuthorized, fetchResortsForAuthState])

  const getResortBykey = React.useCallback((resortKey: string): Resort | undefined => allResorts.find((resort) => resort.resourceKey === resortKey), [allResorts])

  const getResortById = React.useCallback((resortId: string): Resort | undefined => allResorts.find((resort) => resort.productHubId === resortId), [allResorts])

  // Update displayResorts when availResorts or filteredResorts change
  React.useEffect(() => {
    const resortsToMap = availResorts.length > 0 ? availResorts : filteredResorts
    const mappedResorts = resortsToMap.map((r) => mapResortToIResort(transformResortMedia(r)))
    setDisplayResorts(mappedResorts)

    // Turn off data processing loading state when processing is complete
    setIsDataProcessing(false)
  }, [availResorts, filteredResorts])

  const resortContextValue = React.useMemo(
    () => ({
      // Provide both names for compatibility
      resorts: allResorts,
      allResorts,
      availResorts,
      filteredResorts,
      displayResorts,
      isLoading,
      isDataProcessing,
      setResorts: setAllResorts,
      setAllResorts,
      updateAllResorts,
      setAvailResorts,
      setFilteredResorts,
      setIsDataProcessing,
      getResortBykey,
      getResortById,
    }),
    [allResorts, availResorts, filteredResorts, displayResorts, isLoading, isDataProcessing, updateAllResorts, getResortBykey, getResortById],
  )

  return <ResortContext.Provider value={resortContextValue}>{children}</ResortContext.Provider>
}
